
import React, { useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  height?: number;
  className?: string;
  disabled?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder = 'Enter your content here...',
  height = 300,
  className,
  disabled = false
}: RichTextEditorProps) {
  const editorRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [keyFetchError, setKeyFetchError] = useState<string | null>(null);
  const [tinymceFailed, setTinymceFailed] = useState(false);
  const { toast } = useToast();

  // Fetch TinyMCE API key from backend
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        console.log('[RichTextEditor] Starting API key fetch...');
        
        const { data, error } = await supabase.functions.invoke('get-tinymce-key');
        
        console.log('[RichTextEditor] Edge function response data:', data);
        console.log('[RichTextEditor] Edge function response error:', error);
        
        if (error) {
          throw new Error(`Failed to fetch TinyMCE API key: ${error.message}`);
        }
        
        if (data && data.apiKey && data.apiKey !== 'no-api-key') {
          console.log('[RichTextEditor] API key received successfully');
          console.log('[RichTextEditor] API key length:', data.apiKey.length);
          console.log('[RichTextEditor] API key first 10 chars:', data.apiKey.substring(0, 10));
          console.log('[RichTextEditor] API key type:', typeof data.apiKey);
          setApiKey(data.apiKey);
          setKeyFetchError(null);
        } else {
          console.warn('[RichTextEditor] No valid API key in response:', data);
          setKeyFetchError('No valid API key received from server');
          setTinymceFailed(true);
        }
      } catch (error) {
        console.error('[RichTextEditor] Error fetching TinyMCE API key:', error);
        setKeyFetchError(error.message);
        setTinymceFailed(true);
        toast({
          title: 'Editor Warning',
          description: 'Using fallback editor mode due to API key issue',
          variant: 'destructive',
        });
      } finally {
        setIsLoaded(true);
      }
    };

    fetchApiKey();
  }, [toast]);

  const handleEditorInit = (evt: any, editor: any) => {
    console.log('[RichTextEditor] Editor initialized');
    console.log('[RichTextEditor] Editor ID:', editor.id);
    console.log('[RichTextEditor] API key used:', apiKey);
    editorRef.current = editor;
    
    // Check if editor is in readonly mode due to invalid API key
    setTimeout(() => {
      if (editor.getParam('readonly') && !disabled) {
        console.log('[RichTextEditor] Editor is readonly due to invalid API key, switching to fallback');
        setTinymceFailed(true);
      }
    }, 2000);
  };

  const handleEditorError = (error: any) => {
    console.error('[RichTextEditor] TinyMCE error:', error);
    setTinymceFailed(true);
    toast({
      title: 'Editor Error',
      description: 'Switching to plain text editor due to an issue with TinyMCE.',
      variant: 'destructive',
    });
  };

  // Show fallback if TinyMCE failed or no valid API key
  const shouldShowFallback = isLoaded && (tinymceFailed || !apiKey || apiKey === 'no-api-key');
  const shouldRenderTinyMCE = isLoaded && apiKey && apiKey !== 'no-api-key' && !tinymceFailed;

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label htmlFor="rich-text-editor">{label}</Label>}
      
      {keyFetchError && !tinymceFailed && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          Key fetch error: {keyFetchError}
        </div>
      )}
      
      <div className="border rounded-md">
        {!isLoaded ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <div className="text-xs text-gray-500 text-center">
              Loading editor...
            </div>
          </div>
        ) : shouldShowFallback ? (
          <div className="p-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
              <div className="text-sm text-amber-800">
                <strong>HTML Editor:</strong> Rich text features are not available. You can edit HTML markup directly.
              </div>
            </div>
            <textarea
              value={value} // Show HTML markup as-is
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full min-h-[200px] p-3 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              style={{ height: `${height - 100}px` }}
            />
          </div>
        ) : shouldRenderTinyMCE ? (
          <Editor
            apiKey={apiKey}
            onInit={handleEditorInit}
            initialValue={value}
            disabled={disabled}
            init={{
              height,
              menubar: false,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              placeholder,
              branding: false,
              elementpath: false,
              statusbar: false,
              promotion: false,
              readonly: disabled,
              setup: (editor: any) => {
                console.log('[RichTextEditor] Editor setup called');
                editor.on('LoadContent', () => {
                  console.log('[RichTextEditor] Content loaded');
                });
                editor.on('init', () => {
                  console.log('[RichTextEditor] Editor init event fired');
                });
              }
            }}
            onEditorChange={(newValue) => {
              onChange(newValue);
            }}
            onLoadContent={() => {
              console.log('[RichTextEditor] Content loaded');
            }}
          />
        ) : null}
      </div>
      
      {isLoaded && shouldShowFallback && (
        <div className="text-xs text-gray-500">
          Editor running in HTML markup mode
        </div>
      )}
      
      {isLoaded && shouldRenderTinyMCE && (
        <div className="text-xs text-gray-500">
          Rich text editor loaded
        </div>
      )}
    </div>
  );
}
